import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck, Printer } from "lucide-react";
import { COMPLIANCE_OFFICER } from "@/config/compliance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAST_UPDATED = "February 15, 2026";
const EFFECTIVE_DATE = "February 15, 2026";

const sections = [
  { id: "who-we-are", label: "Who We Are" },
  { id: "what-we-collect", label: "What We Collect" },
  { id: "how-we-use-it", label: "How We Use It" },
  { id: "sharing", label: "Who We Share It With" },
  { id: "retention", label: "How Long We Keep It" },
  { id: "your-rights", label: "Your Rights" },
  { id: "security", label: "How We Protect It" },
  { id: "cookies", label: "Cookies & Sessions" },
  { id: "children", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const [version, setVersion] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/legal/current/privacy`)
      .then(({ data }) => { if (alive) setVersion(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              data-testid="privacy-back-btn"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              data-testid="privacy-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-10 md:h-11 w-auto rounded-lg shadow-md"
              />
            </button>
          </div>
          <Button
            data-testid="privacy-print-btn"
            onClick={() => window.print()}
            variant="outline"
            className="rounded-full font-body"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span className="text-sm font-body font-semibold text-primary">Privacy Notice</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary leading-tight mb-4">
            Your privacy, in plain English.
          </h1>
          <p className="text-lg text-text-secondary font-body max-w-2xl leading-relaxed">
            We don't hide behind legalese. This page tells you exactly what Tyvena collects,
            what we do with it, and the controls you have — with no dark patterns and no data sold.
          </p>
          <div className="text-sm text-text-secondary font-body mt-4 flex flex-wrap gap-x-6 gap-y-1">
            <span><span className="font-semibold">Effective:</span> {EFFECTIVE_DATE}</span>
            <span><span className="font-semibold">Last updated:</span> {LAST_UPDATED}</span>
            {version && (
              <span data-testid="privacy-version-chip">
                <span className="font-semibold">Version:</span> {version.version}
              </span>
            )}
          </div>
        </div>

        {/* TOC */}
        <nav aria-label="Table of contents" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 mb-10 print:hidden">
          <h2 className="text-sm font-body font-bold uppercase tracking-wider text-text-secondary mb-3">On this page</h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 list-decimal list-inside">
            {sections.map((s) => (
              <li key={s.id} className="text-sm font-body">
                <a href={`#${s.id}`} className="text-primary hover:underline">{s.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Content */}
        <article className="space-y-10 text-text-primary">
          <Section id="who-we-are" title="1. Who We Are">
            <p>
              <strong>Tyvena</strong> is a consumer-facing care-navigation platform — think of it as
              a GPS for the care space. Families use Tyvena (and our guide, Asher) to find and
              organize home care, home health, hospice, adult day care, senior advisors, and community
              partners near them.
            </p>
            <p>
              Tyvena is operated by <strong>{COMPLIANCE_OFFICER.name}</strong> ({COMPLIANCE_OFFICER.location})
              as the data controller. If you have any privacy question or request,
              <a href={`mailto:${COMPLIANCE_OFFICER.email}`} className="text-primary underline"> {COMPLIANCE_OFFICER.email} </a>
              reaches a real person.
            </p>
          </Section>

          <Section id="what-we-collect" title="2. What We Collect">
            <p>Only what's needed to help you navigate care:</p>
            <BulletList>
              <li><strong>Account info</strong> — name, email, hashed password.</li>
              <li><strong>Care-recipient details</strong> — the first name (or nickname) of the person you're helping care for. Encrypted at rest.</li>
              <li><strong>Provider selections</strong> — which providers you choose, and the signed forms you generate to share with your doctor / case manager.</li>
              <li><strong>Chat conversations with Asher</strong> — the questions you ask and the recommendations Asher gives. Encrypted at rest.</li>
              <li><strong>Consult sessions</strong> — records that a video consult happened, participants, and timestamps (no video/audio recording is stored).</li>
              <li><strong>Audit trail</strong> — a log of security-relevant actions (login, signature, form send, data export). We keep this so we can show you exactly what happened on your account.</li>
              <li><strong>Technical basics</strong> — IP address on sensitive actions, browser type, and the session cookie that keeps you logged in.</li>
            </BulletList>
            <p className="text-sm text-text-secondary italic">
              We do <strong>not</strong> ask for or want SSNs, insurance card numbers, diagnosis codes, or detailed medical records.
              See our <a href="/legal/hipaa-notice" className="text-primary underline">HIPAA Notice</a> for what to avoid entering.
            </p>
          </Section>

          <Section id="how-we-use-it" title="3. How We Use It">
            <BulletList>
              <li>Match you with providers in your ZIP or town.</li>
              <li>Let Asher give you personalized suggestions based on what you've told her.</li>
              <li>Generate provider-choice forms you can print or email to your doctor.</li>
              <li>Notify providers you've chosen that a new family has selected them (so they can follow up).</li>
              <li>Keep your account and payments secure (fraud detection, brute-force lockouts).</li>
              <li>Improve the service — always in aggregate, never by reading individual conversations for training.</li>
            </BulletList>
            <p><strong>We never sell your data.</strong> We never share it with advertisers. We never train third-party AI models on your conversations.</p>
          </Section>

          <Section id="sharing" title="4. Who We Share It With">
            <p>Only three groups of people ever see any of your data:</p>
            <BulletList>
              <li><strong>Providers you explicitly choose and email.</strong> When you sign and send a care-provider selection form, the recipient (your doctor, case manager, or the provider themselves) receives the form and the care-recipient's first name.</li>
              <li><strong>Our carefully-selected service processors</strong>, only where required to operate the site:
                <ul className="list-disc list-inside ml-5 mt-1 space-y-0.5">
                  <li><strong>MongoDB Atlas</strong> — encrypted database hosting.</li>
                  <li><strong>Stripe</strong> — payment processing for providers (never for families).</li>
                  <li><strong>Resend</strong> — transactional email delivery.</li>
                  <li><strong>Daily.co</strong> — WebRTC infrastructure for video consults (no recording is retained by us).</li>
                  <li><strong>OpenAI (via a privacy-preserving proxy)</strong> — powers Asher. Conversation content is sent for the sole purpose of generating your next response and is not used for training.</li>
                </ul>
              </li>
              <li><strong>Law enforcement</strong> — only when we're legally required (subpoena, court order), and we'll notify you if the law permits.</li>
            </BulletList>
          </Section>

          <Section id="retention" title="5. How Long We Keep It">
            <BulletList>
              <li><strong>Your account & personal data:</strong> for as long as your account is open. Delete your account at any time from your Family Portal → Your Data Rights → Delete My Account, and everything (profile, forms, consult records, chat history, audit log) is wiped within seconds.</li>
              <li><strong>Sent forms:</strong> once emailed to a provider, their copy is out of our control — Tyvena can't retract an email already delivered.</li>
              <li><strong>Deletion receipt:</strong> a small non-PII record (masked email, timestamp, counts of records removed) is kept for our support & audit — it doesn't identify you.</li>
              <li><strong>Backups:</strong> encrypted database backups roll over every 7 days.</li>
            </BulletList>
          </Section>

          <Section id="your-rights" title="6. Your Rights">
            <p>You are always in control. From the Family Portal you can, at any time:</p>
            <BulletList>
              <li><strong>Access</strong> — download a complete JSON export of everything Tyvena stores about you (Portal → Your Data Rights → Download My Data).</li>
              <li><strong>Correct</strong> — update your name, email, or care-recipient name in your profile.</li>
              <li><strong>De-identify</strong> — remove your identifying info from provider records while keeping your account (Portal → Privacy & Sharing → Remove me from provider records).</li>
              <li><strong>Delete</strong> — permanently erase your account and every associated record (Portal → Your Data Rights → Delete My Account).</li>
              <li><strong>Object / restrict</strong> — email us at <a href={`mailto:${COMPLIANCE_OFFICER.email}`} className="text-primary underline">{COMPLIANCE_OFFICER.email}</a> for any request our UI doesn't cover.</li>
            </BulletList>
            <p>These rights apply to residents of California (CCPA/CPRA), Europe (GDPR), and every other place we operate.</p>
          </Section>

          <Section id="security" title="7. How We Protect It">
            <BulletList>
              <li><strong>Encryption in transit</strong> — HTTPS/TLS 1.2+ on every page and API call.</li>
              <li><strong>Encryption at rest</strong> — care-recipient names, signed forms, and Asher chat messages are stored with Fernet (AES-128-CBC + HMAC-SHA256) — we can rotate keys without downtime.</li>
              <li><strong>Password hashing</strong> — bcrypt, salted, industry-standard.</li>
              <li><strong>Session security</strong> — HttpOnly, Secure, SameSite cookies; JWTs expire in 1 hour with refresh tokens rotating every 7 days.</li>
              <li><strong>Brute-force protection</strong> — 5 failed logins → 15-minute lockout per IP+email combination.</li>
              <li><strong>Audit logs</strong> — every sensitive action (login, sign, send, export, delete, admin PHI view) is logged with an immutable timestamp and IP.</li>
              <li><strong>Least-privilege access</strong> — only platform administrators can see cross-family records, and every admin PHI view is itself audited.</li>
            </BulletList>
          </Section>

          <Section id="cookies" title="8. Cookies & Sessions">
            <p>Tyvena uses <strong>only</strong> cookies that are essential to running the site:</p>
            <BulletList>
              <li><code>access_token</code>, <code>refresh_token</code>, <code>session_token</code> — keep you signed in. HttpOnly, Secure, SameSite.</li>
            </BulletList>
            <p>
              We do <strong>not</strong> use advertising cookies, third-party trackers, Google Analytics, Facebook Pixel, or any tool that profiles you across the web. If we ever add analytics, it will be a self-hosted, privacy-respecting solution and we'll update this page first.
            </p>
          </Section>

          <Section id="children" title="9. Children's Privacy">
            <p>
              Tyvena is designed for adult family caregivers. We don't knowingly collect data from anyone under 13. If you believe a child under 13 has created an account, email us at
              <a href={`mailto:${COMPLIANCE_OFFICER.email}`} className="text-primary underline"> {COMPLIANCE_OFFICER.email} </a>
              and we'll delete it immediately.
            </p>
          </Section>

          <Section id="changes" title="10. Changes to This Policy">
            <p>
              If we ever change something meaningful, we'll email every active account and update the "Last updated" date above at least 14 days before the change takes effect. Small edits (typos, clarifications) may happen without notice.
            </p>
          </Section>

          <Section id="contact" title="11. Contact Us">
            <p>The fastest way to reach a real human about anything on this page:</p>
            <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5 my-4">
              <p className="font-body font-semibold text-text-primary">{COMPLIANCE_OFFICER.name} — {COMPLIANCE_OFFICER.title}</p>
              <p className="font-body text-sm text-text-secondary mt-1">
                Email: <a href={`mailto:${COMPLIANCE_OFFICER.email}`} className="text-primary underline">{COMPLIANCE_OFFICER.email}</a>
              </p>
              <p className="font-body text-sm text-text-secondary">{COMPLIANCE_OFFICER.location}</p>
            </div>
            <p className="text-sm text-text-secondary italic">
              Because Tyvena is currently in an interim launch without a BAA-covered hosting arrangement, please avoid emailing us actual medical records, SSNs, or diagnosis codes — reach out first and we'll direct you to a secure channel if needed. See our <a href="/legal/hipaa-notice" className="text-primary underline">HIPAA Notice</a> for details.
            </p>
          </Section>
        </article>

        <div className="mt-12 pt-8 border-t border-[#E2E8F0] flex flex-wrap items-center gap-3 justify-between print:hidden">
          <a href="/legal/hipaa-notice" data-testid="privacy-to-hipaa" className="text-sm font-body text-primary hover:underline">→ Read our HIPAA Notice</a>
          <p className="text-xs text-text-secondary font-body">© {new Date().getFullYear()} Tyvena · A guide for the care journey</p>
        </div>
      </main>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-text-primary mb-4">{title}</h2>
      <div className="space-y-4 font-body text-base leading-relaxed text-text-primary">
        {children}
      </div>
    </section>
  );
}

function BulletList({ children }) {
  return <ul className="list-disc list-outside ml-6 space-y-2 font-body text-base leading-relaxed">{children}</ul>;
}
